import { FC } from 'react'
import { Box } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { Layout } from '@/apps/latest/components/templates/Layout'
import { sendAttachment } from '@/apps/latest/features/attachments/attachmentUploads'
import { NewCustomerForm } from '@/apps/latest/features/nutrition/NewCustomerForm'
import type { NewCustomerState } from '@/apps/latest/features/nutrition/NutritionSidebar'
import { renderNutritionSidebar } from '@/apps/latest/features/nutrition/nutritionShell'
import { useStartNutritionRound } from '@/apps/latest/features/nutrition/useNutritionCustomers'
import type { KarteState } from '@/apps/latest/pages/nutrition/customer'
import { useToast } from '@/shared/contexts/ToastContext'

// 新しいお客様の登録 (/nutrition/new)。左ペインの「＋ 新しいお客様を登録」の行から来る (絞りの字を state で受ける)。
// 4 欄は本文に出す (左ペインの幅に 4 欄・読み取り・似た候補は入らない)。登録は相談の始まりなので、同じ 1 手で今回を始め、
// 問診票を今回の資料にしてカルテ (/nutrition/customers/:id) を開き、そのまま録音を始める (design-book ADR-0084 決定 1・
// ADR-0086 決定 3)。始めた記録は問診票が届くまで中身の無い器で回の一覧に来ないので、id をカルテへ渡す。
const NutritionNewCustomerPage: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const startRound = useStartNutritionRound()
  const text = (location.state as NewCustomerState | null)?.text ?? ''

  const openKarte = (customerId: string, state?: KarteState) =>
    navigate(`/nutrition/customers/${customerId}`, { state })

  return (
    <Layout
      breadcrumb={['新しいお客様']}
      renderSidebar={renderNutritionSidebar}
    >
      <Box sx={{ maxWidth: 560 }}>
        <NewCustomerForm
          // 絞りの字が替わったら (左ペインから押し直した) 欄を作り直す
          key={`newCustomer-${text}`}
          initialText={text}
          onCancel={() => navigate(-1)}
          onSelectExisting={openKarte}
          onRegistered={(customer, questionnaire) =>
            startRound.mutate(customer, {
              // 問診票は送り終わりを待たずに移る (送れなければカルテの添付資料に残り、そこで送り直せる)
              onSuccess: (eventId) => {
                if (questionnaire)
                  void sendAttachment(queryClient, eventId, questionnaire)
                openKarte(customer.id, { started: eventId })
              },
              onError: () => showToast('記録を作れませんでした', 'error'),
            })
          }
          busy={startRound.isPending}
        />
      </Box>
    </Layout>
  )
}

export default NutritionNewCustomerPage
